/**
 * Creator Home (CR-01). Profile beside earnings, then every open campaign
 * with the search, sort and min-CPM filters. The other overview tiles
 * live on Performance.
 */
import { useEffect, useState } from "react";
import { Card, LinkButton } from "@ui/primitives";
import { fontSize } from "@design/index";
import { api, type SessionResponse } from "@app/shared/api";
import { CreatorAvatar, Icons } from "@app/shared/icons";
import { CampaignBrowse } from "./Discover";
import { VerifyBanner } from "@app/shared/AuthScreens";
import { CreatorEarningsTile } from "./CreatorStats";

interface CreatorProfile {
  display_name?: string; legal_payout_name?: string | null; country?: string | null;
  timezone?: string | null; bio?: string | null; discoverable?: boolean;
  date_of_birth?: string | null; public_fields?: string[];
  avatar_href?: string | null;
}

interface SubmissionRow { id: string; state: string }

function memberSince(iso: string | null | undefined): string {
  if (!iso) return "this month";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "this month";
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

export function Home({
  session, onOpenCampaign,
}: { session: SessionResponse; onOpenCampaign: (id: string) => void }) {
  const verified = session.user?.emailVerified ?? false;
  const name = session.user?.displayName ?? "creator";

  const [profile, setProfile] = useState<CreatorProfile | null>(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [submissions, setSubmissions] = useState<SubmissionRow[] | null>(null);

  useEffect(() => {
    void api.get<{ profile: CreatorProfile | null; complete?: boolean }>("/onboarding/creator").then((r) => {
      if (r.ok) {
        setProfile(r.data.profile);
        setProfileComplete(Boolean(r.data.complete));
      }
    });
    void api.get<{ submissions: SubmissionRow[] }>("/submissions").then((r) => {
      setSubmissions(r.ok ? (r.data.submissions ?? []) : []);
    });
  }, []);

  const display = profile?.display_name || name;
  const subs = submissions ?? [];
  const approved = subs.filter((s) => s.state === "APPROVED").length;
  const rejected = subs.filter((s) => s.state === "REJECTED" || s.state === "DISQUALIFIED").length;
  const decided = approved + rejected;
  const approval = decided === 0 ? "N/A" : `${Math.round((approved / decided) * 100)}%`;

  return (
    <>
      {!verified && session.user ? <VerifyBanner email={session.user.email} /> : null}

      <h1 style={{
        margin: 0, fontSize: 28, fontWeight: 750, letterSpacing: "-0.03em", lineHeight: 1.15,
      }}>
        Welcome Back, {display}
      </h1>

      <div
        className="cm-home-cols"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        <Card padding={0} style={{ overflow: "hidden", borderRadius: 18, height: "100%" }}>
          <div style={{ padding: "22px 22px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <CreatorAvatar src={profile?.avatar_href} name={display} size={58} />
              <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: fontSize.h3, fontWeight: 700, letterSpacing: "-0.02em" }}>
                  {display}
                </p>
                <p style={{
                  margin: 0, fontSize: fontSize.label, color: "var(--muted)",
                  display: "flex", flexWrap: "wrap", gap: "8px 16px", alignItems: "center",
                }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "var(--faint)" }}>{Icons.member}</span>
                    member since {memberSince(session.user?.createdAt)}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "var(--faint)" }}>{Icons.approval}</span>
                    Approval Rate: {approval}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <a
            href="/onboarding"
            className={profileComplete ? undefined : "cm-profile-cta"}
            style={{
              margin: "14px 16px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 14px",
              borderRadius: 12,
              background: profileComplete ? "var(--surface-2)" : "var(--accent-soft)",
              color: profileComplete ? "var(--text)" : "var(--accent-soft-fg)",
              textDecoration: "none",
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ display: "block", fontSize: fontSize.bodyLg, fontWeight: 700 }}>
                {profileComplete ? "Edit profile" : "Complete Profile"}
              </strong>
              <span className="cm-profile-cta-muted" style={{ fontSize: fontSize.label, color: "var(--muted)" }}>
                {profileComplete
                  ? "Update the details brands and payouts use"
                  : "Add the details brands and payouts need"}
              </span>
            </span>
            <span style={{ color: "var(--accent)" }}>{Icons.arrow}</span>
          </a>
        </Card>

        <CreatorEarningsTile fill />
      </div>

      <section aria-labelledby="active-campaigns-h" style={{ display: "grid", gap: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 id="active-campaigns-h" style={{
            margin: 0, fontSize: 28, fontWeight: 750, letterSpacing: "-0.03em", lineHeight: 1.15,
          }}>
            Active Campaigns
          </h2>
          <LinkButton href="/work" variant="primary" size="sm" style={{
            gap: 8, borderRadius: 10, padding: "8px 12px", fontWeight: 700,
          }}>
            <span style={{ display: "inline-flex" }}>{Icons.campaigns}</span>
            My Campaigns
          </LinkButton>
        </div>
        <CampaignBrowse onOpen={onOpenCampaign} />
      </section>
    </>
  );
}
